import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { acquireModalScrollLock, ROUTE_DISMISS_MODALS_EVENT } from '../../utils/modalUtils';

/**
 * Shell command palette — navigate to app routes (Ctrl/Cmd + K).
 */
const ShellCommandPalette = memo(function ShellCommandPalette({
  isOpen,
  onClose,
  navItems = [],
  placeholder = 'Search pages…',
  emptyLabel = 'No pages found',
  shortcutHint = 'Ctrl+K',
  dir,
}) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter((item) => String(item.label || '').toLowerCase().includes(q));
  }, [navItems, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setActiveIndex(0);
      return undefined;
    }
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return acquireModalScrollLock();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onRouteDismiss = () => onClose();
    window.addEventListener(ROUTE_DISMISS_MODALS_EVENT, onRouteDismiss);
    return () => window.removeEventListener(ROUTE_DISMISS_MODALS_EVENT, onRouteDismiss);
  }, [isOpen, onClose]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const selectItem = useCallback(
    (item) => {
      if (!item?.path) return;
      navigate(item.path);
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (event.key === 'Enter' && filtered[activeIndex]) {
        event.preventDefault();
        selectItem(filtered[activeIndex]);
      }
    },
    [activeIndex, filtered, onClose, selectItem]
  );

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="shell-command-palette-backdrop ads-dialog-backdrop modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="shell-command-palette ads-dialog modal-content"
        role="dialog"
        aria-modal="true"
        aria-label={placeholder}
        dir={dir}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="shell-command-palette__search">
          <Search size={18} className="shell-command-palette__search-icon" aria-hidden />
          <input
            ref={inputRef}
            type="search"
            className="shell-command-palette__input ads-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="shell-command-palette__kbd">{shortcutHint}</kbd>
        </div>
        <ul className="shell-command-palette__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="shell-command-palette__empty">{emptyLabel}</li>
          ) : (
            filtered.map((item, index) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`shell-command-palette__item${
                      index === activeIndex ? ' shell-command-palette__item--active' : ''
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => selectItem(item)}
                  >
                    {Icon ? <Icon size={18} strokeWidth={1.35} aria-hidden /> : null}
                    <span className="shell-command-palette__label">{item.label}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>,
    document.body
  );
});

export default ShellCommandPalette;
